// Button feedback everywhere, without touching each button:
//  - a click remembers which <button> was pressed, and every api() call its handler makes
//    (including ones after an `await`, and refetches it triggers) marks that button busy: a spinner
//    via CSS on [aria-busy], and further clicks are swallowed so nothing is sent twice;
//  - a short vibration on touch devices that support it.
// The CSS lives in index.css (button press, [aria-busy] spinner). Keep in sync with the admin copy.

let current: HTMLElement | null = null;
const inflight = new WeakMap<HTMLElement, number>();
/** Short requests still show the spinner long enough to register. */
const MIN_BUSY_MS = 300;

/** Link api() calls made in the rest of this task (and its microtasks) to `btn`. */
function arm(btn: HTMLElement) {
  current = btn;
  setTimeout(() => {
    if (current === btn) current = null;
  }, 0);
}

export function installPressFeedback() {
  document.addEventListener(
    'click',
    (e) => {
      const btn = (e.target as Element | null)?.closest?.('button');
      if (!btn) return;
      if (btn.getAttribute('aria-busy') === 'true') {
        // Still working on the last press: don't run the handler again.
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      arm(btn);
    },
    true,
  );
  document.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType !== 'touch') return;
      const btn = (e.target as Element | null)?.closest?.('button');
      if (btn && !btn.disabled) navigator.vibrate?.(8);
    },
    { capture: true, passive: true },
  );
}

/** Called by api(): if a button press started this request, show it as busy until it settles. */
export function trackPress<T>(p: Promise<T>): Promise<T> {
  const btn = current;
  if (!btn) return p;
  const n = (inflight.get(btn) ?? 0) + 1;
  inflight.set(btn, n);
  if (n === 1) btn.setAttribute('aria-busy', 'true');
  const started = Date.now();
  const settle = () => {
    // Runs before the caller's own continuation, so a follow-up api() call links to the same button
    // and the spinner doesn't blink off between steps.
    arm(btn);
    setTimeout(
      () => {
        const left = (inflight.get(btn) ?? 1) - 1;
        inflight.set(btn, left);
        if (left <= 0) btn.removeAttribute('aria-busy');
      },
      Math.max(30, MIN_BUSY_MS - (Date.now() - started)),
    );
  };
  p.then(settle, settle);
  return p;
}
